"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.adminPlanCountFilterSchema = exports.adminPlanFilterSchema = void 0;
const QueryBuilder_1 = require("../QueryBuilder");
const operator_time_ledger_1 = require("./operator-time-ledger");
const STRING_ALLOWED_OPS = [
    'contains',
    'doesNotContain',
    'equals',
    'doesNotEqual',
    'isAnyOf',
    'startsWith',
    'endsWith',
    'isEmpty',
    'isNotEmpty',
];
const TIMESTAMP_ALLOWED_OPS = ['greaterThan', 'lessThan'];
const TRAINING_PLAN_EXECUTION_MODE = operator_time_ledger_1.EVAL_EXECUTION_MODE;
const NEGATIVE_STRING_OPERATORS = ['doesNotContain', 'doesNotEqual', 'not'];
const sharedPlanFields = {
    isTrainingPlan: {
        description: `True iff the plan's executionMode is "${TRAINING_PLAN_EXECUTION_MODE}".`,
        options: {
            type: QueryBuilder_1.FieldType.Custom,
            buildWhere: buildIsTrainingPlanWhere,
            buildOrderBy: (direction) => [{ executionMode: direction }],
        },
        allowedOperators: ['is'],
    },
    createdAt: {
        description: 'Plan creation time. Filter value is a millisecond timestamp.',
        options: {
            type: QueryBuilder_1.FieldType.Custom,
            buildWhere: buildTimestampWhere('createdAt'),
            buildOrderBy: (direction) => [{ createdAt: direction }],
        },
        allowedOperators: TIMESTAMP_ALLOWED_OPS,
    },
    deadlineAt: {
        description: 'Plan deadline. Filter value is a millisecond timestamp.',
        options: {
            type: QueryBuilder_1.FieldType.Custom,
            buildWhere: buildTimestampWhere('deadlineAt'),
            buildOrderBy: (direction) => [{ deadlineAt: direction }],
        },
        allowedOperators: TIMESTAMP_ALLOWED_OPS,
    },
    completedAt: {
        description: 'Plan completion time. Null for plans that have not yet completed. Filter value is a millisecond timestamp.',
        options: {
            type: QueryBuilder_1.FieldType.Custom,
            buildWhere: buildTimestampWhere('completedAt'),
            buildOrderBy: (direction) => [{ completedAt: direction }],
        },
        allowedOperators: [...TIMESTAMP_ALLOWED_OPS, 'isEmpty', 'isNotEmpty'],
    },
};
exports.adminPlanFilterSchema = (0, QueryBuilder_1.createFilterSchema)({
    fields: {
        id: {
            description: 'Plan ID (a `CadstromUid`, e.g., "wizplan_…").',
            options: { type: QueryBuilder_1.FieldType.String },
            allowedOperators: ['contains', 'doesNotContain', 'equals', 'doesNotEqual', 'isAnyOf', 'startsWith', 'endsWith'],
        },
        packageId: {
            description: 'ID of the package the plan operates on (a `CadstromUid`, e.g., "pkg_…"). Null for plans with no package.',
            options: { type: QueryBuilder_1.FieldType.String },
            allowedOperators: ['contains', 'doesNotContain', 'equals', 'doesNotEqual', 'isAnyOf', 'startsWith', 'endsWith'],
        },
        status: {
            description: 'Plan status: "pending", "complete", "failed", or "cancelled".',
            options: { type: QueryBuilder_1.FieldType.String },
            allowedOperators: ['is', 'not', 'isAnyOf'],
        },
        generator: {
            description: 'Generator name.',
            options: { type: QueryBuilder_1.FieldType.String, column: 'generatorName' },
            allowedOperators: ['contains', 'doesNotContain', 'equals', 'doesNotEqual', 'isAnyOf', 'startsWith', 'endsWith'],
        },
        assignedToUserName: {
            description: 'Display name or username of the assigned user. Searches both displayName and username.',
            options: {
                type: QueryBuilder_1.FieldType.Custom,
                buildWhere: buildPersonNameWhere('assignedToUser', 'assignedToUserId'),
                buildOrderBy: (direction) => [{ assignedToUser: { displayName: direction } }],
            },
            allowedOperators: STRING_ALLOWED_OPS,
        },
        assignedToOrgName: {
            description: 'Display name or username of the assigned organization. Searches both displayName and username.',
            options: {
                type: QueryBuilder_1.FieldType.Custom,
                buildWhere: buildPersonNameWhere('assignedToOrg', 'assignedToOrgId'),
                buildOrderBy: (direction) => [{ assignedToOrg: { displayName: direction } }],
            },
            allowedOperators: STRING_ALLOWED_OPS,
        },
        completedByUserNames: {
            description: 'Display names or usernames of all users that completed a fragment of this plan.',
            options: {
                type: QueryBuilder_1.FieldType.Custom,
                buildWhere: buildCompletedByUserNamesWhere,
                buildOrderBy: () => [],
            },
            allowedOperators: ['contains', 'doesNotContain', 'isEmpty', 'isNotEmpty'],
        },
        associatedUserNames: {
            description: 'Display names or usernames of "associated" users to the plan: the `completedBy` users of the plan\'s fragments, as well as the plan\'s `assignedToUserId`.',
            options: {
                type: QueryBuilder_1.FieldType.Custom,
                buildWhere: buildAssociatedUserNameWhere,
                buildOrderBy: (direction) => [{ assignedToUser: { displayName: direction } }],
            },
            allowedOperators: STRING_ALLOWED_OPS,
        },
        manufacturerPartNumber: {
            description: 'Manufacturer part number of the linked package.',
            options: {
                type: QueryBuilder_1.FieldType.Custom,
                buildWhere: (operator, value) => {
                    if (operator === 'isEmpty') {
                        return { packageId: null };
                    }
                    if (operator === 'isNotEmpty') {
                        return { packageId: { not: null } };
                    }
                    const clause = (0, QueryBuilder_1.buildStringOperatorClause)('manufacturerPartNumber', operator, value);
                    if (clause === null) {
                        return {};
                    }
                    if (NEGATIVE_STRING_OPERATORS.includes(operator)) {
                        return { OR: [{ packageId: null }, { package: { is: clause } }] };
                    }
                    return { package: { is: clause } };
                },
                buildOrderBy: (direction) => [{ package: { manufacturerPartNumber: direction } }],
            },
            allowedOperators: STRING_ALLOWED_OPS,
        },
        ...sharedPlanFields,
    },
    ignoredFields: ['actions'],
});
exports.adminPlanCountFilterSchema = (0, QueryBuilder_1.createFilterSchema)({
    fields: { ...sharedPlanFields },
    ignoredFields: [],
});
function buildIsTrainingPlanWhere(operator, value) {
    const isTrue = value === 'true';
    const isFalse = value === 'false';
    const notTrainingPlan = { OR: [{ executionMode: null }, { executionMode: { not: TRAINING_PLAN_EXECUTION_MODE } }] };
    switch (operator) {
        case 'is':
        case 'equals': {
            if (isTrue) {
                return { executionMode: TRAINING_PLAN_EXECUTION_MODE };
            }
            if (isFalse) {
                return notTrainingPlan;
            }
            return {};
        }
        case 'not':
        case 'doesNotEqual': {
            if (isTrue) {
                return notTrainingPlan;
            }
            if (isFalse) {
                return { executionMode: TRAINING_PLAN_EXECUTION_MODE };
            }
            return {};
        }
        default:
            return {};
    }
}
function buildTimestampWhere(column) {
    return (operator, value) => {
        if (operator === 'isEmpty') {
            return { [column]: null };
        }
        if (operator === 'isNotEmpty') {
            return { [column]: { not: null } };
        }
        if (value === undefined || value === '') {
            return {};
        }
        const ms = Number(value);
        if (!Number.isFinite(ms)) {
            return {};
        }
        const date = new Date(ms);
        switch (operator) {
            case 'equals':
            case 'is':
                return { [column]: date };
            case 'not':
            case 'doesNotEqual':
                return { NOT: { [column]: date } };
            case 'greaterThan':
                return { [column]: { gt: date } };
            case 'greaterThanOrEqual':
                return { [column]: { gte: date } };
            case 'lessThan':
                return { [column]: { lt: date } };
            case 'lessThanOrEqual':
                return { [column]: { lte: date } };
            default:
                return {};
        }
    };
}
function buildPersonNameWhere(relation, idField) {
    return (operator, value) => {
        if (operator === 'isEmpty') {
            return { [idField]: null };
        }
        if (operator === 'isNotEmpty') {
            return { [idField]: { not: null } };
        }
        const buildPositiveOr = (op, opValue, extraProps) => ({
            [relation]: {
                is: {
                    OR: [{ displayName: { [op]: opValue, ...extraProps } }, { username: { [op]: opValue, ...extraProps } }],
                },
            },
        });
        const buildNegativeAnd = (op, opValue, extraProps) => ({
            OR: [
                { [idField]: null },
                {
                    [relation]: {
                        is: {
                            AND: [
                                { NOT: { displayName: { [op]: opValue, ...extraProps } } },
                                { NOT: { username: { [op]: opValue, ...extraProps } } },
                            ],
                        },
                    },
                },
            ],
        });
        switch (operator) {
            case 'contains':
                return buildPositiveOr('contains', value, { mode: 'insensitive' });
            case 'doesNotContain':
                return buildNegativeAnd('contains', value, { mode: 'insensitive' });
            case 'equals':
            case 'is':
                return buildPositiveOr('equals', value);
            case 'doesNotEqual':
            case 'not':
                return buildNegativeAnd('equals', value);
            case 'startsWith':
                return buildPositiveOr('startsWith', value, { mode: 'insensitive' });
            case 'endsWith':
                return buildPositiveOr('endsWith', value, { mode: 'insensitive' });
            case 'isAnyOf': {
                try {
                    return buildPositiveOr('in', (0, QueryBuilder_1.parseIsAnyOfValues)(value));
                }
                catch {
                    return {};
                }
            }
            default:
                return {};
        }
    };
}
function buildCompletedByUserNamesWhere(operator, value) {
    if (operator === 'isEmpty') {
        return { fragments: { none: { completedByUserId: { not: null } } } };
    }
    if (operator === 'isNotEmpty') {
        return { fragments: { some: { completedByUserId: { not: null } } } };
    }
    const nameMatch = {
        OR: [
            { displayName: { contains: value, mode: 'insensitive' } },
            { username: { contains: value, mode: 'insensitive' } },
        ],
    };
    if (operator === 'contains') {
        return { fragments: { some: { completedBy: { is: nameMatch } } } };
    }
    if (operator === 'doesNotContain') {
        return { fragments: { none: { completedBy: { is: nameMatch } } } };
    }
    return {};
}
function buildAssociatedUserNameWhere(operator, value) {
    if (operator === 'isEmpty') {
        return { fragments: { none: { completedByUserId: { not: null } } }, assignedToUserId: null };
    }
    if (operator === 'isNotEmpty') {
        return { OR: [{ fragments: { some: { completedByUserId: { not: null } } } }, { assignedToUserId: { not: null } }] };
    }
    const fragmentNameMatch = (op, val, extra) => ({
        fragments: {
            some: {
                completedBy: { is: { OR: [{ displayName: { [op]: val, ...extra } }, { username: { [op]: val, ...extra } }] } },
            },
        },
    });
    const assignedUserNameMatch = (op, val, extra) => ({
        assignedToUser: { is: { OR: [{ displayName: { [op]: val, ...extra } }, { username: { [op]: val, ...extra } }] } },
    });
    const assignedUserNameNoMatch = (op, val, extra) => ({
        assignedToUser: {
            is: { AND: [{ NOT: { displayName: { [op]: val, ...extra } } }, { NOT: { username: { [op]: val, ...extra } } }] },
        },
    });
    const buildPositive = (op, val, extra) => ({
        OR: [
            fragmentNameMatch(op, val, extra),
            {
                fragments: { none: { completedByUserId: { not: null } } },
                ...assignedUserNameMatch(op, val, extra),
            },
        ],
    });
    const buildNegative = (op, val, extra) => ({
        AND: [
            {
                fragments: {
                    none: {
                        completedBy: {
                            is: { OR: [{ displayName: { [op]: val, ...extra } }, { username: { [op]: val, ...extra } }] },
                        },
                    },
                },
            },
            {
                OR: [
                    { fragments: { some: { completedByUserId: { not: null } } } },
                    { assignedToUserId: null },
                    assignedUserNameNoMatch(op, val, extra),
                ],
            },
        ],
    });
    switch (operator) {
        case 'contains':
            return buildPositive('contains', value, { mode: 'insensitive' });
        case 'doesNotContain':
            return buildNegative('contains', value, { mode: 'insensitive' });
        case 'equals':
        case 'is':
            return buildPositive('equals', value);
        case 'doesNotEqual':
        case 'not':
            return buildNegative('equals', value);
        case 'startsWith':
            return buildPositive('startsWith', value, { mode: 'insensitive' });
        case 'endsWith':
            return buildPositive('endsWith', value, { mode: 'insensitive' });
        case 'isAnyOf': {
            try {
                return buildPositive('in', (0, QueryBuilder_1.parseIsAnyOfValues)(value));
            }
            catch {
                return {};
            }
        }
        default:
            return {};
    }
}
//# sourceMappingURL=admin-plan-filter-schema.js.map